import { TimeoutError } from "./errors";
import { LightSerialPort } from "./light-serial-port";
import type { DataSource } from "./types";

abstract class BaseDataListener {
  private listening = false;
  private listenerCount = 0;
  private abortController: AbortController | null = null;
  protected eventTarget = new EventTarget();

  constructor(protected readonly serialPort: LightSerialPort, protected readonly readTimeout = 2000) {
  }

  protected addEventListener(type: 'data', callback: EventListenerOrEventListenerObject | null, options?: AddEventListenerOptions | boolean): void {
    this.eventTarget.addEventListener(type, callback, options);
    this.listenerCount++;
    if (!this.listening) {
      this.startListening();
    }
  }

  protected removeEventListener(type: 'data', callback: EventListenerOrEventListenerObject | null, options?: EventListenerOptions | boolean): void {
    this.eventTarget.removeEventListener(type, callback, options);
    this.listenerCount = Math.max(0, this.listenerCount - 1);
    if (this.listenerCount === 0) {
      this.stopListening();
    }
  }

  private startListening(): void {
    this.listening = true;
    this.abortController = new AbortController();
    this.readLoop(this.abortController);
  }

  private stopListening(): void {
    this.listening = false;
    this.abortController?.abort();
    this.abortController = null;
  }

  private async readLoop(abortController: AbortController): Promise<void> {
    while (this.listening) {
      try {
        const data = await this.serialPort.read(this.readTimeout, abortController);
        this.emitData(data);
      } catch (e) {
        if (e instanceof TimeoutError) {
          continue;
        }
        break;
      }
    }
  }

  protected abstract emitData(_data: Uint8Array): void;
}

export class StringDataListener extends BaseDataListener implements DataSource {
  private readonly decoder = new TextDecoder();
  constructor(serialPort: LightSerialPort, { decoder, readTimeout }: { decoder?: TextDecoder, readTimeout?: number } = {}) {
    super(serialPort, readTimeout);
    if (decoder) {
      this.decoder = decoder;
    }
  }

  protected emitData(data: Uint8Array): void {
    console.log('emitData', this.decoder.decode(data));
    this.eventTarget.dispatchEvent(new MessageEvent('data', { data: this.decoder.decode(data) }));
  }

  onData(callback: (data: string) => void): () => void {
    console.log('onData register');
    const handler = (e: Event) => callback((e as MessageEvent).data);
    this.addEventListener('data', handler);
    return () => this.removeEventListener('data', handler);
  }
}
